import { defineAsyncComponent } from '../../lib/utils/vue3compat/vue.js?vue3';
import { GlAlert } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { sortBy } from 'lodash-es';
import produce from 'immer';
import Draggable from '../../lib/utils/vue3compat/draggable_compat.vue?vue3';
import BoardAddNewColumn from '../../../../../ee/app/assets/javascripts/boards/components/board_add_new_column.vue?vue3';
import BoardAddNewColumnTrigger from './board_add_new_column_trigger.vue?vue3';
import WorkItemDetailPanel from '../../work_items/components/work_item_detail_panel.vue?vue3';
import { s__ } from '~/locale';
import { removeParams, updateHistory } from '~/lib/utils/url_utility';
import { defaultSortableOptions, DRAG_DELAY } from '~/sortable/constants';
import { mapWorkItemWidgetsToIssuableFields } from '../../work_items/list/utils.js?vue3';
import { DraggableItemTypes, flashAnimationDuration, listsQuery, updateListQueries, ListType, listIssuablesQueries, DEFAULT_BOARD_LIST_ITEMS_SIZE, BoardType } from 'ee_else_ce/boards/constants';
import { DETAIL_VIEW_QUERY_PARAM_NAME, VIEW_CONTEXT } from '~/work_items/constants';
import { calculateNewPosition } from 'ee_else_ce/boards/boards_util';
import { setError } from '../graphql/cache_updates.js?vue3';
import BoardColumn from './board_column.vue?vue3';
import BoardDrawerWrapper from './board_drawer_wrapper.vue?vue3';
export default {
    name: 'BoardContent',
    draggableItemTypes: DraggableItemTypes,
    VIEW_CONTEXT,
    components: {
        BoardAddNewColumn,
        BoardAddNewColumnTrigger,
        BoardColumn,
        BoardDrawerWrapper,
        EpicsSwimlanes: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/boards/components/epics_swimlanes.vue?vue3')),
        GlAlert,
        WorkItemDetailPanel
    },
    inject: [
        'boardType',
        'canAdminList',
        'disabled',
        'issuableType',
        'isGroupBoard',
        'fullPath'
    ],
    props: {
        boardId: {
            type: String,
            required: true
        },
        filterParams: {
            type: Object,
            required: true
        },
        isSwimlanesOn: {
            type: Boolean,
            required: true
        },
        boardLists: {
            type: Object,
            required: false,
            default: ()=>{}
        },
        error: {
            type: String,
            required: false,
            default: null
        },
        listQueryVariables: {
            type: Object,
            required: true
        },
        addColumnFormVisible: {
            type: Boolean,
            required: true
        }
    },
    emits: [
        'drawer-closed',
        'drawer-opened',
        'set-active-list',
        'set-add-column-form-visibility',
        'set-filters'
    ],
    data () {
        return {
            highlightedLists: [],
            columnsThatCannotFindActiveItem: 0,
            draggedItemId: null,
            focusedListId: null
        };
    },
    computed: {
        boardListsById () {
            return this.boardLists;
        },
        boardListsToUse () {
            const lists = this.boardLists;
            return sortBy([
                ...Object.values(lists)
            ], 'position');
        },
        visibleBoardLists () {
            return this.boardListsToUse.filter((list)=>!list.collapsed);
        },
        effectiveFocusedListId () {
            if (this.focusedListId && this.visibleBoardLists.some((l)=>l.id === this.focusedListId)) {
                return this.focusedListId;
            }
            return this.visibleBoardLists[0]?.id ?? null;
        },
        canDragColumns () {
            return this.canAdminList;
        },
        boardColumnWrapper () {
            return this.canDragColumns ? Draggable : 'div';
        },
        draggableOptions () {
            const options = {
                ...defaultSortableOptions,
                disabled: this.disabled,
                draggable: '.is-draggable',
                fallbackOnBody: false,
                group: 'boards-list',
                tag: 'div',
                value: this.boardListsToUse,
                delay: DRAG_DELAY,
                delayOnTouchOnly: true,
                filter: 'input',
                preventOnFilter: false
            };
            return this.canDragColumns ? options : {};
        },
        backlogListId () {
            const backlogList = this.boardListsToUse.find((list)=>list.listType === ListType.backlog);
            return backlogList?.id || '';
        },
        closedListId () {
            const closedList = this.boardListsToUse.find((list)=>list.listType === ListType.closed);
            return closedList?.id || '';
        },
        namespace () {
            return this.isGroupBoard ? BoardType.group : BoardType.project;
        }
    },
    watch: {
        addColumnFormVisible (visible) {
            if (visible && this.isSwimlanesOn) {
                this.$nextTick(()=>{
                    this.afterFormEnters();
                });
            }
        }
    },
    methods: {
        focusAdjacentList (currentListId, direction) {
            const lists = this.visibleBoardLists;
            const currentIndex = lists.findIndex((list)=>list.id === currentListId);
            if (currentIndex === -1) return;
            const targetIndex = currentIndex + direction;
            if (targetIndex < 0 || targetIndex >= lists.length) return;
            this.focusedListId = lists[targetIndex].id;
        },
        afterFormEnters () {
            const formEl = this.$refs.addColumnForm?.$el;
            if (formEl) {
                formEl.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'end',
                    block: 'nearest'
                });
            }
        },
        highlightList (listId) {
            this.highlightedLists.push(listId);
            setTimeout(()=>{
                this.highlightedLists = this.highlightedLists.filter((id)=>id !== listId);
            }, flashAnimationDuration);
        },
        dismissError () {
            setError({
                message: null,
                captureError: false
            });
        },
        async updateListPosition ({ item: { dataset: { listId: movedListId, draggableItemType } }, newIndex, to: { children } }) {
            if (draggableItemType !== DraggableItemTypes.list) {
                return;
            }
            const displacedListId = children[newIndex].dataset.listId;
            if (movedListId === displacedListId) {
                return;
            }
            const initialPosition = this.boardListsById[movedListId].position;
            const targetPosition = this.boardListsById[displacedListId].position;
            try {
                await this.$apollo.mutate({
                    mutation: updateListQueries[this.issuableType].mutation,
                    variables: {
                        listId: movedListId,
                        position: targetPosition
                    },
                    update: (store)=>{
                        const sourceData = store.readQuery({
                            query: listsQuery[this.issuableType].query,
                            variables: this.listQueryVariables
                        });
                        const data = produce(sourceData, (draftData)=>{
                            // for current list, new position is already set by Apollo via automatic update
                            const affectedNodes = draftData[this.boardType].board.lists.nodes.filter((node)=>node.id !== movedListId);
                            affectedNodes.forEach((node)=>{
                                // eslint-disable-next-line no-param-reassign
                                node.position = calculateNewPosition(node.position, initialPosition, targetPosition);
                            });
                        });
                        store.writeQuery({
                            query: listsQuery[this.issuableType].query,
                            variables: this.listQueryVariables,
                            data
                        });
                    },
                    optimisticResponse: {
                        updateBoardList: {
                            __typename: 'UpdateBoardListPayload',
                            errors: [],
                            list: {
                                ...this.boardLists[movedListId],
                                position: targetPosition
                            }
                        }
                    }
                });
            } catch (error) {
                setError({
                    error,
                    message: s__('Boards|An error occurred while moving the list. Please try again.')
                });
            }
        },
        updateBoardCard (workItem, activeCard) {
            const { cache } = this.$apollo.provider.clients.defaultClient;
            const variables = {
                id: activeCard.listId,
                filters: this.filterParams,
                fullPath: this.fullPath,
                boardId: this.boardId,
                isGroup: this.isGroupBoard,
                isProject: !this.isGroupBoard,
                first: DEFAULT_BOARD_LIST_ITEMS_SIZE
            };
            cache.updateQuery({
                query: listIssuablesQueries[this.issuableType].query,
                variables
            }, (boardList)=>mapWorkItemWidgetsToIssuableFields({
                    list: boardList,
                    workItem,
                    isBoard: true,
                    namespace: this.namespace,
                    type: this.issuableType
                }));
        },
        isLastList (index) {
            return this.boardListsToUse.length - 1 === index;
        },
        handleCannotFindActiveItem () {
            this.columnsThatCannotFindActiveItem += 1;
            if (this.columnsThatCannotFindActiveItem === this.boardListsToUse.length) {
                updateHistory({
                    url: removeParams([
                        DETAIL_VIEW_QUERY_PARAM_NAME
                    ])
                });
            }
        },
        handleDragStart ({ itemId }) {
            this.draggedItemId = itemId;
        },
        handleDragStop () {
            this.draggedItemId = null;
        }
    }
};
